// Tabela de fechamento ("sign-off vivo"): mesmas colunas em todos os recortes —
// NS Qtd, % no Prazo e Corte, cada um com Hoje / MTD / M-1 / Referência e Δ (MTD vs M-1).
// Clique no valor MTD (ou no "?" do cabeçalho) abre a explicação com os números daquela linha.
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import ExplicaKpi, { TipoKpi } from './ExplicaKpi';
import { Kpis, KpisJanelas, SignoffLinha } from '../types';
import { fmtMoney, fmtPct, fmtPp } from '../utils/format';

type CampoKpi = 'ns_qtd' | 'pct_no_prazo' | 'pct_corte';

interface KpiDef {
  titulo: string;
  abrev: string;
  tipo: TipoKpi;
  campo: CampoKpi;
  fmt: (n: number | null | undefined) => string;
  fmtDelta: (n: number | null | undefined) => string;
  direcao: 'up' | 'down';
  extra?: { campo: keyof Kpis; fmt: (n: number | null | undefined) => string; sufixo?: string };
}

const KPIS: KpiDef[] = [
  {
    titulo: 'NS Quantidade', abrev: 'NS', tipo: 'ns', campo: 'ns_qtd', fmt: fmtPct, fmtDelta: fmtPp, direcao: 'up',
    extra: { campo: 'pct_apurado', fmt: fmtPct, sufixo: ' apurado' }, // quanto do volume já entrou no cálculo
  },
  { titulo: '% no Prazo', abrev: 'Prazo', tipo: 'prazo', campo: 'pct_no_prazo', fmt: fmtPct, fmtDelta: fmtPp, direcao: 'up' },
  {
    titulo: 'Corte', abrev: 'Corte', tipo: 'corte', campo: 'pct_corte', fmt: fmtPct, fmtDelta: fmtPp, direcao: 'down',
    extra: { campo: 'corte_valor', fmt: fmtMoney },
  },
];

function Delta({ mtd, m1, direcao, fmtDelta }: { mtd: number | null; m1: number | null; direcao: 'up' | 'down'; fmtDelta: KpiDef['fmtDelta'] }) {
  if (mtd === null || m1 === null) return <span className="muted">—</span>;
  const d = mtd - m1;
  if (Math.abs(d) < 1e-9) return <span className="muted">=</span>;
  const favoravel = direcao === 'up' ? d > 0 : d < 0;
  return (
    <span className={favoravel ? 'delta-good' : 'delta-bad'}>
      {d > 0 ? '▲' : '▼'} {fmtDelta(Math.abs(d))}
    </span>
  );
}

interface Props {
  total: SignoffLinha | null;
  linhas: SignoffLinha[];
  refLabel: string;
  m1Label?: string;
}

interface Explicando {
  kpi: KpiDef;
  label: string;
  kpis: KpisJanelas;
}

export default function SignoffTable({ total, linhas, refLabel, m1Label }: Props) {
  const [explicando, setExplicando] = useState<Explicando | null>(null);

  const abrir = (kpi: KpiDef, linha: SignoffLinha) =>
    setExplicando({ kpi, label: linha.label, kpis: linha.kpis });

  const CelulasKpi = ({ linha, kpi }: { linha: SignoffLinha; kpi: KpiDef }) => {
    const kpis = linha.kpis;
    const v = (w: 'hoje' | 'mtd' | 'm1' | 'ref') => kpis[w][kpi.campo];
    return (
      <>
        <td className="num sep">{kpi.fmt(v('hoje'))}</td>
        <td className="num mtd">
          <button
            className="kpi-click"
            title="Clique para ver como este número foi calculado"
            onClick={() => abrir(kpi, linha)}
          >
            {kpi.fmt(v('mtd'))}
          </button>
          {kpi.extra && (
            <div className="muted text-[10.5px]">
              {kpi.extra.fmt(kpis.mtd[kpi.extra.campo] as number)}{kpi.extra.sufixo || ''}
            </div>
          )}
        </td>
        <td className="num">{kpi.fmt(v('m1'))}</td>
        <td className="num">{kpi.fmt(v('ref'))}</td>
        <td className="num"><Delta mtd={v('mtd')} m1={v('m1')} direcao={kpi.direcao} fmtDelta={kpi.fmtDelta} /></td>
      </>
    );
  };

  return (
    <>
      {/* desktop: a tabela de fechamento */}
      <div className="table-wrap desktop-only">
        <table className="signoff">
          <thead>
            <tr>
              <th rowSpan={2} className="align-bottom">Recorte</th>
              {KPIS.map((k) => (
                <th key={k.titulo} colSpan={5} className="group sep">
                  <span className="inline-flex items-center gap-1.5">
                    {k.titulo}
                    {total && (
                      <button className="dica-btn" aria-label={`Como é calculado ${k.titulo}?`} onClick={() => abrir(k, total)}>
                        <HelpCircle size={13} />
                      </button>
                    )}
                  </span>
                </th>
              ))}
            </tr>
            <tr>
              {KPIS.map((k) => (
                <Fragment key={k.titulo}>
                  <th className="num sub sep">Hoje</th>
                  <th className="num sub mtd">MTD</th>
                  <th className="num sub" title="Comparação justa: mês anterior até o mesmo dia">{m1Label || 'M-1'}</th>
                  <th className="num sub">{refLabel}</th>
                  <th className="num sub">Δ M-1</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.key}>
                <td>
                  {l.link ? <Link to={`/service-level${l.link}`}><strong>{l.label}</strong></Link> : <strong>{l.label}</strong>}
                  {l.sublabel && <div className="muted text-[11px]">{l.sublabel}</div>}
                </td>
                {KPIS.map((k) => <CelulasKpi key={k.titulo} linha={l} kpi={k} />)}
              </tr>
            ))}
            {total && (
              <tr className="total-row">
                <td><strong>{total.label}</strong></td>
                {KPIS.map((k) => <CelulasKpi key={k.titulo} linha={total} kpi={k} />)}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* mobile: cards compactos — toque no valor abre a explicação com os números.
          O card mostra o mês corrente; quem não teve movimento no mês cai para a
          janela de referência (senão o card apareceria todo com trações). */}
      <div className="mobile-only">
        {linhas.map((l) => {
          // 3 situações distintas — nenhuma pode parecer "zerada" sem explicação:
          //   sem pedido no mês      → mostra a janela de referência
          //   pedido sem apuração    → mantém o mês (NS/Corte ainda não existem)
          //   normal                 → mês corrente com Δ vs M-1
          const semMes = !(Number(l.kpis.mtd.pedidos_recebidos) > 0 || Number(l.kpis.mtd.qtd_comprada) > 0);
          const semApuracao = !semMes && !(Number(l.kpis.mtd.qtd_comprada) > 0);
          const janela = semMes ? 'ref' : 'mtd';
          return (
            <Link key={l.key} to={l.link ? `/service-level${l.link}` : '#'} className="ranking-card items-stretch">
              <div className="flex-1">
                <div className="nome">{l.label}</div>
                {semMes ? (
                  <div className="sub">sem movimento no mês · {refLabel}</div>
                ) : semApuracao ? (
                  <div className="sub">
                    {l.kpis.mtd.pedidos_recebidos} pedido(s) no mês, nada faturado ainda
                  </div>
                ) : (
                  l.sublabel && <div className="sub">{l.sublabel}</div>
                )}
              </div>
              <div className="flex gap-3.5">
                {KPIS.map((k) => (
                  <div key={k.titulo} className="text-right">
                    <div className="sub">{k.abrev}</div>
                    <button
                      className="kpi-click font-bold tabular-nums"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); abrir(k, l); }}
                    >
                      {k.fmt(l.kpis[janela][k.campo])}
                    </button>
                    {!semMes && (
                      <div className="text-[11px]">
                        <Delta mtd={l.kpis.mtd[k.campo]} m1={l.kpis.m1[k.campo]} direcao={k.direcao} fmtDelta={k.fmtDelta} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Link>
          );
        })}
      </div>

      {explicando && (
        <ExplicaKpi
          tipo={explicando.kpi.tipo}
          label={explicando.label}
          kpis={explicando.kpis}
          m1Label={m1Label}
          onClose={() => setExplicando(null)}
        />
      )}
    </>
  );
}
